import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

type TestCategory =
  | "All Tests"
  | "NEET"
  | "JEE Main"
  | "JEE"
  | "Foundation"
  | "Class 7"
  | "Class 8"
  | "Free Tests"
  | "Premium Tests";

type TestAccess = "Free" | "Premium" | "Live";
type TestDifficulty = "Easy" | "Medium" | "Hard";

type StudentInfo = {
  id: number | null;
  fullName: string;
  email: string;
  studentClass: string;
  course: string;
};

type TestSection = {
  id?: number;
  name?: string;
  subject?: string;
  questions?: number;
  marksPerQuestion?: number;
  sectionOrder?: number;
};

type BackendTestSeries = {
  id: number;
  title: string;
  category: string;
  course: string;
  duration: number;
  totalQuestions: number;
  totalMarks: number;
  negativeMarks?: number;
  attempts?: number;
  students?: number;
  startDate?: string;
  endDate?: string;
  status?: string;
  price?: string;
  resultMode?: string;
  proctoring?: boolean;
  shuffleQuestions?: boolean;
  sections?: TestSection[];
  instructions?: string;
};

type TestItem = {
  id: number;
  title: string;
  exam: string;
  className: string;
  subject: string;
  access: TestAccess;
  difficulty: TestDifficulty;
  questions: number;
  duration: number;
  marks: number;
  attemptedBy: string;
  avgAccuracy: number;
  status: string;
  course: string;
  resultMode: string;
  startDate?: string;
  endDate?: string;
};

// const PROFILE_API = "http://localhost:9090/api/profile";
// const STUDENT_TESTS_API = "http://localhost:9091/api/student/tests";

const PROFILE_API = "https://node-api.mediitacademy.com/api/profile";
const STUDENT_TESTS_API = "https://api.mediitacademy.com/api/student/tests";

const filters: TestCategory[] = [
  "All Tests",
  "NEET",
  "JEE Main",
  "Foundation",
  "Class 7",
  "Class 8",
  "Free Tests",
  "Premium Tests",
];

const TestDashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get("courseId") || "";

  const [student, setStudent] = useState<StudentInfo>({
    id: null,
    fullName: "",
    email: "",
    studentClass: "",
    course: "",
  });

  const [tests, setTests] = useState<TestItem[]>([]);
  const [activeFilter, setActiveFilter] = useState<TestCategory>("All Tests");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");

  const getToken = () => localStorage.getItem("token") || "";

  const getStoredStudentId = () => {
    const directId =
      localStorage.getItem("studentId") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("id");

    if (directId) return Number(directId);

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return Number(user?.id || user?.studentId || 0);
    } catch {
      return 0;
    }
  };

  const normalizeExam = (value: string) => {
    const text = String(value || "").trim();
    const lower = text.toLowerCase();

    if (lower.includes("jee")) return "JEE Main";
    if (lower.includes("neet")) return "NEET";
    if (lower.includes("class 7")) return "Class 7";
    if (lower.includes("class 8")) return "Class 8";
    if (lower.includes("foundation")) return "Foundation";

    return text || "Foundation";
  };

  const normalizeAccess = (
    price: string | undefined,
    status: string | undefined
  ): TestAccess => {
    const priceText = String(price || "Free").toLowerCase();
    const statusText = String(status || "").toLowerCase();

    if (statusText === "published") {
      if (priceText.includes("free") || priceText === "0") return "Free";
      return "Premium";
    }

    return "Live";
  };

  const normalizeSubjects = (sections?: TestSection[]) => {
    if (!sections || sections.length === 0) return "General";

    const subjects = sections
      .map((section) => section.subject || section.name || "")
      .filter(Boolean);

    return subjects.length > 0 ? subjects.join(", ") : "General";
  };

  const normalizeDifficulty = (totalQuestions: number): TestDifficulty => {
    if (totalQuestions <= 40) return "Easy";
    if (totalQuestions <= 100) return "Medium";
    return "Hard";
  };

  const normalizeTest = (item: BackendTestSeries): TestItem => {
    const questions = Number(item.totalQuestions || 0);
    const status = item.status || "Published";

    return {
      id: Number(item.id),
      title: item.title || "Test Series",
      exam: normalizeExam(item.category || item.course || ""),
      className: item.course || item.category || "Course Test",
      subject: normalizeSubjects(item.sections),
      access: normalizeAccess(item.price, status),
      difficulty: normalizeDifficulty(questions),
      questions,
      duration: Number(item.duration || 0),
      marks: Number(item.totalMarks || 0),
      attemptedBy: Number(item.attempts || 0).toLocaleString(),
      avgAccuracy: 0,
      status,
      course: item.course || "",
      resultMode: item.resultMode || "Instant",
      startDate: item.startDate,
      endDate: item.endDate,
    };
  };

  const fetchProfile = async () => {
    const token = getToken();

    if (!token) {
      window.location.href = "/login";
      return null;
    }

    const response = await fetch(PROFILE_API, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || "Profile loading failed");
    }

    const nextStudent: StudentInfo = {
      id: Number(data.id || data.user_id || getStoredStudentId()),
      fullName: data.full_name || data.name || "Student",
      email: data.email || "",
      studentClass: data.school || data.studentClass || data.class || "",
      course: data.course || "",
    };

    setStudent(nextStudent);
    localStorage.setItem("studentId", String(nextStudent.id || ""));

    return nextStudent;
  };

  const fetchStudentTests = async (courseName: string) => {
    const token = getToken();
    const url = `${STUDENT_TESTS_API}?course=${encodeURIComponent(
      courseName || ""
    )}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data?.message || `Test series API failed (${response.status})`
      );
    }

    const normalized = Array.isArray(data) ? data.map(normalizeTest) : [];
    setTests(normalized);
  };

  const loadData = async () => {
    setLoading(true);
    setApiError("");

    try {
      const profile = await fetchProfile();
      await fetchStudentTests(profile?.course || "");
    } catch (error: any) {
      console.error("Student tests loading error:", error);
      setApiError(error?.message || "Student tests loading failed");
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredTests = useMemo(() => {
    const text = search.toLowerCase().trim();

    return tests.filter((item) => {
      const matchesSearch =
        !text ||
        `${item.title} ${item.exam} ${item.className} ${item.subject} ${item.course}`
          .toLowerCase()
          .includes(text);

      const matchesFilter =
        activeFilter === "All Tests" ||
        item.exam === activeFilter ||
        item.exam === activeFilter.replace("JEE", "JEE Main") ||
        item.className.toLowerCase().includes(activeFilter.toLowerCase()) ||
        (activeFilter === "Free Tests" && item.access === "Free") ||
        (activeFilter === "Premium Tests" && item.access === "Premium");

      return matchesSearch && matchesFilter;
    });
  }, [tests, activeFilter, search]);

  const startTest = (test: TestItem) => {
    window.location.href = `/student-dashboard/quiz/${test.id}?courseId=${courseId}`;
  };

  const totalQuestions = tests.reduce((sum, item) => sum + item.questions, 0);
  const totalAttempts = tests.reduce(
    (sum, item) => sum + Number(String(item.attemptedBy).replace(/,/g, "") || 0),
    0
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f7ff]">
        <div className="rounded-[28px] bg-white px-8 py-7 text-center shadow-sm">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-blue-100 border-t-blue-700" />
          <p className="mt-4 text-sm font-black text-slate-600">
            Loading test series...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f7ff] text-[#101827] font-['Inter']">
      <header className="border-b border-slate-200/80 bg-white px-4 py-5 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-[1280px] flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <button
              onClick={() =>
                courseId
                  ? (window.location.href = `/student-dashboard/course/${courseId}?tab=tests`)
                  : (window.location.href = "/student-dashboard")
              }
              className="mb-3 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 hover:bg-blue-50"
            >
              ← Back
            </button>

            <p className="text-sm font-black uppercase tracking-wide text-blue-700">
              {student.course || "Student Tests"}
            </p>

            <h1 className="mt-1 text-3xl font-black tracking-[-0.04em] text-slate-950">
              Test Dashboard
            </h1>

            <p className="mt-1 text-sm font-semibold text-slate-500">
              Aapke enrolled course ke published tests yaha show honge.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() =>
                alert("Reports backend connect hone ke baad yaha show honge.")
              }
              className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-700 hover:bg-slate-50"
            >
              My Reports
            </button>

            <button
              onClick={loadData}
              className="rounded-2xl bg-[#0f3f70] px-5 py-3 text-sm font-black text-white hover:bg-blue-700"
            >
              Refresh Tests
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1280px] p-4 sm:p-6 lg:p-8">
        {apiError && (
          <div className="mb-5 rounded-[22px] border border-red-100 bg-red-50 p-4 text-sm font-black text-red-700">
            {apiError}
          </div>
        )}

        <section className="grid gap-5 md:grid-cols-4">
          <MetricCard
            title="Available Tests"
            value={String(tests.length)}
            desc="Published for your course"
          />
          <MetricCard
            title="Total Questions"
            value={String(totalQuestions)}
            desc="Across available tests"
          />
          <MetricCard
            title="Total Attempts"
            value={totalAttempts.toLocaleString()}
            desc="Student submissions"
          />
          <MetricCard
            title="Access"
            value={student.course ? "Active" : "Demo"}
            desc={student.studentClass || "Student"}
          />
        </section>

        <section className="mt-6 grid gap-6 lg:grid-cols-[280px_1fr]">
          <aside className="rounded-[24px] bg-white p-5 shadow-sm">
            <h2 className="text-xl font-black text-slate-950">Filters</h2>

            <div className="mt-5 space-y-2">
              {filters.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`w-full rounded-2xl px-4 py-3 text-left text-sm font-black transition ${
                    activeFilter === filter
                      ? "bg-[#0f3f70] text-white"
                      : "text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>

            <div className="mt-8 rounded-2xl bg-blue-50 p-4">
              <h3 className="text-sm font-black text-blue-900">
                Secure Test Mode
              </h3>
              <p className="mt-2 text-xs font-semibold leading-5 text-blue-700">
                Fullscreen, tab switch warning, answer auto-save aur submit lock ke
                saath.
              </p>
            </div>
          </aside>

          <section>
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <h2 className="text-2xl font-black text-slate-950">
                  Available Test Series
                </h2>
                <p className="mt-1 text-sm font-semibold text-slate-500">
                  Student yaha se test select karke attempt karega.
                </p>
              </div>

              <input
                id="test-search"
                name="test-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search test..."
                className="h-12 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 outline-none focus:border-blue-300 md:w-[320px]"
              />
            </div>

            <div className="mt-5 space-y-4">
              {filteredTests.map((test) => (
                <TestCard key={test.id} test={test} startTest={startTest} />
              ))}

              {filteredTests.length === 0 && (
                <div className="rounded-[24px] border border-dashed border-slate-200 bg-white p-10 text-center">
                  <h3 className="text-xl font-black text-slate-950">
                    No test found
                  </h3>
                  <p className="mt-2 text-sm font-semibold text-slate-500">
                    Admin ne abhi is course ke liye published test add nahi kiya
                    ya filter/search match nahi hua.
                  </p>
                </div>
              )}
            </div>
          </section>
        </section>
      </main>
    </div>
  );
};

const MetricCard = ({
  title,
  value,
  desc,
}: {
  title: string;
  value: string;
  desc: string;
}) => {
  return (
    <div className="rounded-[22px] bg-white p-6 shadow-sm">
      <p className="text-sm font-black text-slate-500">{title}</p>
      <h2 className="mt-2 text-3xl font-black text-slate-950">{value}</h2>
      <p className="mt-1 text-xs font-semibold text-slate-500">{desc}</p>
    </div>
  );
};

const TestCard = ({
  test,
  startTest,
}: {
  test: TestItem;
  startTest: (test: TestItem) => void;
}) => {
  return (
    <article className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="grid gap-4 lg:grid-cols-[72px_1fr_130px_130px_130px_130px] lg:items-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-xl font-black text-[#0f3f70]">
          {test.exam.slice(0, 2)}
        </div>

        <div>
          <div className="flex flex-wrap gap-2">
            <Badge label={test.exam} />
            <Badge
              label={test.access}
              tone={test.access === "Premium" ? "gold" : "green"}
            />
            <Badge
              label={test.difficulty}
              tone={test.difficulty === "Hard" ? "purple" : "blue"}
            />
          </div>

          <h3 className="mt-3 text-xl font-black text-slate-950">
            {test.title}
          </h3>

          <p className="mt-1 text-sm font-semibold text-slate-500">
            {test.className} • {test.subject}
          </p>

          <p className="mt-2 text-xs font-black text-blue-700">
            Result: {test.resultMode}
          </p>
        </div>

        <SmallStat value={String(test.questions)} label="Questions" />
        <SmallStat value={`${test.duration}m`} label="Duration" />
        <SmallStat value={String(test.marks)} label="Marks" />

        <button
          onClick={() => startTest(test)}
          className="rounded-2xl bg-[#0f3f70] px-5 py-3 text-sm font-black text-white hover:bg-blue-700"
        >
          Start Test
        </button>
      </div>

      <div className="mt-5 flex flex-col gap-2 border-t border-slate-100 pt-4 text-sm font-semibold text-slate-500 md:flex-row md:items-center md:justify-between">
        <span>
          Attempted by{" "}
          <b className="text-slate-950">{test.attemptedBy}</b> students
        </span>
        <span>
          Average accuracy{" "}
          <b className="text-slate-950">{test.avgAccuracy}%</b>
        </span>
      </div>
    </article>
  );
};

const SmallStat = ({ value, label }: { value: string; label: string }) => {
  return (
    <div className="rounded-2xl bg-slate-50 p-4 text-center">
      <p className="text-xl font-black text-slate-950">{value}</p>
      <p className="mt-1 text-xs font-bold text-slate-500">{label}</p>
    </div>
  );
};

const Badge = ({
  label,
  tone = "blue",
}: {
  label: string;
  tone?: "blue" | "green" | "gold" | "purple";
}) => {
  const cls =
    tone === "green"
      ? "bg-emerald-50 text-emerald-700"
      : tone === "gold"
      ? "bg-amber-50 text-amber-700"
      : tone === "purple"
      ? "bg-violet-50 text-violet-700"
      : "bg-blue-50 text-blue-700";

  return (
    <span className={`rounded-full px-3 py-1 text-xs font-black ${cls}`}>
      {label}
    </span>
  );
};

export default TestDashboard;
